'use client'

import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import { IoHeart } from 'react-icons/io5'
import { LuHeart } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'

import CommentsBox from '@/features/veluvit/components/detail/comments-box'
import DetailHero from '@/features/veluvit/components/detail/detail-hero'
import {
  CastBox,
  CommandBox,
  CreatorWorks,
  PlayGuide,
} from '@/features/veluvit/components/detail/detail-sections'
import IntroBox from '@/features/veluvit/components/detail/intro-box'
import OpeningBox from '@/features/veluvit/components/detail/opening-box'
import CollectionBox from '@/features/veluvit/components/detail/collection-box'
import FixedBar from '@/features/veluvit/components/shell/fixed-bar'
import PageHeader from '@/features/veluvit/components/shell/page-header'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import { useAppData } from '@/features/veluvit/hooks/use-app-data'
import { useAppReadScope } from '@/features/veluvit/hooks/use-app-read-scope'
import { CatalogContext } from '@/features/veluvit/hooks/use-catalog'
import { buildLiveCatalog, isAdult } from '@/features/veluvit/lib/live-catalog'
import { saveRef } from '@/features/veluvit/lib/referral'
import { liveWorkSchema } from '@/features/veluvit/schemas/catalog.schema'
import {
  openAppSheet,
  startChat,
} from '@/features/veluvit/services/account.actions'
import { api, ApiError } from '@/features/veluvit/services/api'
import {
  recordWorkView,
  toggleFavorite,
} from '@/features/veluvit/services/catalog.actions'
import { hasLayers } from '@/features/veluvit/stores/overlay.store'
import {
  useRoom,
  useSessionStore,
} from '@/features/veluvit/stores/session.store'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

import type { Work } from '@/features/veluvit/types/api.types'
import type { InitialWork } from '@/features/veluvit/services/work-read.server'

type DetailResult = {
  key: string
  status: 'loading' | 'ready' | 'missing' | 'restricted' | 'error'
  work?: Work
}

/** 초기 작품 목록에 없는 검색 결과만 작품 상세 API에서 읽어 이 화면의 카탈로그에 더한다. */
export default function DetailScreen({
  id,
  initial,
}: {
  id: string
  initial: InitialWork
}) {
  const { catalog, data } = useAppData()
  const scope = useAppReadScope()
  const initialMatch =
    initial.epoch === scope && initial.id === id ? initial : null
  const identity = useSessionStore((state) => state.identity)
  const showAdult = useSessionStore(
    (state) => !!state.data?.user?.settings?.showAdult
  )
  const eligible = useSessionStore(
    (state) => !!state.data?.config?.adult?.eligible
  )
  const [attempt, setAttempt] = useState(0)
  const [result, setResult] = useState<DetailResult | null>(null)
  const key = `${scope}:${identity}:${showAdult}:${eligible}:${id}`
  const inCatalog = !!catalog.chars[id]
  const canShowAdult = showAdult && eligible
  const fetchKey =
    inCatalog || (initialMatch && attempt === 0) ? null : `${key}:${attempt}`
  const [seenFetchKey, setSeenFetchKey] = useState<string | null>(null)
  if (seenFetchKey !== fetchKey) {
    setSeenFetchKey(fetchKey)
    if (fetchKey) setResult({ key, status: 'loading' })
  }

  useEffect(() => {
    if (inCatalog || (initialMatch && attempt === 0)) return
    const controller = new AbortController()
    let active = true
    const sameAccess = () => {
      const session = useSessionStore.getState()
      return (
        active &&
        session.identity === identity &&
        !!session.data?.user?.settings?.showAdult === showAdult &&
        !!session.data?.config?.adult?.eligible === eligible
      )
    }
    void api<unknown>(
      `/work/${encodeURIComponent(id)}/`,
      undefined,
      'GET',
      controller.signal
    )
      .then((value) => {
        if (!sameAccess()) return
        const parsed = liveWorkSchema.safeParse(value)
        if (!parsed.success || parsed.data.id !== id) {
          setResult({ key, status: 'error' })
          return
        }
        const work = parsed.data as Work
        setResult({
          key,
          status: isAdult(work) && !canShowAdult ? 'restricted' : 'ready',
          work,
        })
      })
      .catch((error: unknown) => {
        if (!sameAccess() || controller.signal.aborted) return
        setResult({
          key,
          status:
            error instanceof ApiError && error.status === 404
              ? 'missing'
              : 'error',
        })
      })
    return () => {
      active = false
      controller.abort()
    }
  }, [
    id,
    key,
    identity,
    showAdult,
    eligible,
    canShowAdult,
    inCatalog,
    initialMatch,
    attempt,
  ])

  const localCatalog = useMemo(() => {
    const work =
      result?.key === key && result.status === 'ready'
        ? result.work
        : attempt === 0 && initialMatch?.status === 'ready'
          ? initialMatch.work
          : undefined
    if (!data || !work) return null
    return buildLiveCatalog(
      [...catalog.works.filter((item) => item.id !== id), work],
      data.config,
      data.user?.name ?? '사용자',
      data.ownedCards
    )
  }, [catalog, data, id, key, result, initialMatch, attempt])

  if (inCatalog) {
    if (
      !canShowAdult &&
      (catalog.chars[id].style?.rating === 'unsafe' ||
        catalog.chars[id].badges.includes('19+'))
    )
      return <UnavailableWork restricted />
    return <DetailContent id={id} />
  }
  const status =
    result?.key === key
      ? result.status
      : attempt === 0
        ? initialMatch?.status
        : undefined
  if (!status || status === 'loading')
    return (
      <>
        <PageHeader back title="작품" />
        <p role="status" className="px-4 py-16 text-center text-c14 text-t3">
          작품을 불러오는 중…
        </p>
      </>
    )
  if (
    status === 'restricted' ||
    (status === 'ready' &&
      initialMatch?.work &&
      isAdult(initialMatch.work) &&
      !canShowAdult)
  )
    return <UnavailableWork restricted />
  if (status === 'missing') return <UnavailableWork />
  if (status === 'error' || !localCatalog?.chars[id])
    return (
      <UnavailableWork error onRetry={() => setAttempt((value) => value + 1)} />
    )
  return (
    <CatalogContext.Provider value={localCatalog}>
      <DetailContent id={id} />
    </CatalogContext.Provider>
  )
}

function UnavailableWork({
  restricted = false,
  error = false,
  onRetry,
}: {
  restricted?: boolean
  error?: boolean
  onRetry?: () => void
}) {
  return (
    <>
      <PageHeader back title="작품" />
      <EmptyState
        title={
          error
            ? '작품을 불러오지 못했어요'
            : restricted
              ? '이 작품은 지금 볼 수 없어요'
              : '작품을 찾을 수 없어요'
        }
        description={
          error
            ? '잠시 후 다시 시도해 주세요.'
            : restricted
              ? '계정의 성인 표시 설정과 인증 상태를 확인해 주세요.'
              : '링크가 바뀌었거나 작품이 공개 목록에서 내려갔을 수 있어요.'
        }
        action={
          error ? (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              다시 불러오기
            </Button>
          ) : (
            <Link
              href="/explore"
              className="text-point underline underline-offset-4"
            >
              다른 작품 둘러보기
            </Link>
          )
        }
      />
    </>
  )
}

/**
 * 작품 상세 — 머리(흐린 표지 빛 + 표지 액자 + 상품 글) → 대사 한 줄 → 섹션들(한 줄 컬럼, PC 760).
 * 2026-10-07 갈아엎기 — PC 2단(왼쪽 갤러리 · 오른쪽 상자)과 테두리 상자 묶음을 걷고, 머리부터 댓글까지 한 컬럼으로 흐르게 했다.
 * 2026-10-08 스튜디오 기획 반영 — 섹션은 스튜디오가 입력받는 것만: 작품 소개(description) → 컬렉션(장소 → 장면) → 시작 상황(starts) →
 * 등장인물(여러 명일 때) → 명령어 → 플레이 가이드(playGuide) → 작가의 다른 작품 → 댓글. 옛 레퍼런스 상자(관전 포인트 · 한눈에 보기 · 작가 코멘트 · 함께 만나볼 장면 · 같은 작품 캐릭터 · 고정 문구 즐기는 방법)는 걷었다.
 */
function DetailContent({ id }: { id: string }) {
  const { catalog, data } = useAppData()
  const rooms = useSessionStore((s) => s.data?.rooms)
  const liveRoom = useRoom(id)
  const c = catalog.chars[id]
  const room = rooms ? liveRoom : undefined
  const liked = !!data?.favorites.includes(id)

  // 작가 추천 링크(?ref=작가코드)로 들어왔으면 이 작품에 30일 기억한다 — 대화방을 만들 때 서버로 보내 작가 정산에 반영
  useEffect(() => {
    saveRef(id, new URLSearchParams(location.search).get('ref'))
  }, [id])

  // 조회 기록 — 작가 랭킹 '최다 조회'의 원천. 계정이 생긴 뒤(세션 데이터가 온 뒤)에 한 번
  const hasUser = !!data?.user
  useEffect(() => {
    if (hasUser) recordWorkView(id)
  }, [id, hasUser])

  // 관계가 생긴 뒤에만 30초 뒤 앱 설치를 권한다 — 첫 진입에는 설치할 이유가 없다
  useEffect(() => {
    if (!room || useUiStore.getState().appShown) return
    const timer = setTimeout(() => {
      if (!hasLayers()) openAppSheet(id)
    }, 30000)
    return () => clearTimeout(timer)
  }, [id, room])

  if (!c) return null

  return (
    <div className="pb-30">
      <DetailHero id={id} />
      <div className="mx-auto max-w-[760px] px-4 over10:box-content over10:px-8">
        {/* 대사 — 머리 바로 밑 한 줄. 캐릭터 목소리라 조금 크게. 소개 글은 아래 '작품 소개'(스튜디오 상세 설명)가 맡는다 */}
        {c.quote && (
          <p className="pt-5 text-c16 leading-[1.55] font-semibold text-t1 [overflow-wrap:anywhere] over10:pt-6 over10:text-c17">
            “{c.quote}”
          </p>
        )}
        {/* 섹션 순서 = 스튜디오 작품 입력 순서(2026-10-08): 상세 설명 → 컬렉션 → 시작 설정 → 등장인물 → 명령어 → 플레이 가이드, 그다음 작가의 다른 작품 · 댓글 */}
        <IntroBox id={id} />
        <CollectionBox id={id} />
        <OpeningBox id={id} />
        <CastBox id={id} />
        <CommandBox id={id} />
        <PlayGuide id={id} />
        <CreatorWorks id={id} />
        <CommentsBox id={id} />
      </div>
      <FixedBar align="center">
        <Button
          type="button"
          aria-label={liked ? '관심 작품 해제' : '관심 작품 저장'}
          aria-pressed={liked}
          onClick={() => toggleFavorite(id)}
          className="grid size-12 flex-none cursor-pointer place-items-center rounded-full glass transition-[background-color,scale] duration-200 hover:bg-foreground/[0.12] active:scale-95"
          variant="bare"
        >
          {liked ? (
            <IoHeart aria-hidden="true" className="size-5.5 text-point" />
          ) : (
            <LuHeart aria-hidden="true" className="size-5.5" />
          )}
        </Button>
        <Button
          variant="primary"
          size="lg"
          shape="pill"
          bold
          className="w-full"
          onClick={() => startChat(id, { fresh: true })}
        >
          새 채팅으로 시작
        </Button>
      </FixedBar>
    </div>
  )
}
